// no dangerouslySetInnerHTML — titles are user-controlled text (SEC-REQ-4)
//
// V2 source path: customer/midas/components/v2/history/MidasHistoryRowV2.tsx
// V1 source: none (greenfield — Phase 4)
// Strategy: scratch
// Verified no-MUI: yes
// Phase: 4
//
// Single thread row in the history list. Per the locked product decisions
// (no `last_message_preview` snippet field), each row shows:
//   • Time column (HH:MM + relative day) — JBM mono, fixed width slot
//   • Machine context chip (or "Platform Mode" with terracotta dot)
//   • Sora 14/medium thread title — truncated, full text via `title` attr
//   • Trailing arrow — animates 2px right on hover
//
// Click → onOpen(threadId). Caller is responsible for navigating to
// /midas-v2?thread={id} or whatever transition the page wants.
import React from "react";
import { ArrowRight } from "lucide-react";

import { Thread } from "../../../midasPageModels";

interface MidasHistoryRowV2Props {
  thread: Thread;
  /** Resolved machine display name ("Mazak QT-200") or null for platform threads. */
  machineLabel: string | null;
  /** Click handler — opens the thread. */
  onOpen: (threadId: string) => void;
  /** Translation function — keeps the row stateless and i18n-safe. */
  t: (key: string, fallback: string) => string;
}

/** Format the time column — "HH:MM" + DD.MM date subtitle. */
const formatTimeCol = (iso: string): { time: string; sub: string } => {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const time = `${hh}:${mm}`;

  // §6.10 audit: spec renders DD.MM literally on every row regardless of
  // proximity. The date-group eyebrow already says TODAY / YESTERDAY —
  // having "Today" / "Yesterday" words on individual rows duplicated that
  // signal and added noise. JBM mono so the column lines up uniformly.
  const dd = String(d.getDate()).padStart(2, "0");
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const sub = `${dd}.${mo}`;

  return { time, sub };
};

const MidasHistoryRowV2: React.FC<MidasHistoryRowV2Props> = ({
  thread,
  machineLabel,
  onOpen,
  t,
}) => {
  const { time, sub } = formatTimeCol(
    thread.last_message_at || thread.created_at
  );

  const ariaLabel = `${
    machineLabel || t("midas.historyPage.platformMode", "Platform Mode")
  } — ${thread.title} — ${time} ${sub}`;

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(thread.id)}
        aria-label={ariaLabel}
        title={thread.title}
        // §6.4 audit: card min-height 64 → 76 (spec ~72-80).
        // §6.5 audit: padding 16/12 → 20/16 (px-4 py-3 → px-5 py-4) — closest
        //   Tailwind step to spec's 20/18.
        // §6.19 audit: items-start → items-center so the time column,
        //   title-block, and trailing arrow all baseline-center vertically
        //   (matches spec's tidy row alignment).
        className="group flex min-h-[76px] w-full items-center gap-3 rounded-lg border border-[rgba(44,38,32,0.06)] bg-white px-5 py-4 text-left outline-none transition-all duration-150 hover:border-[rgba(212,87,43,0.30)] hover:bg-[#FCFAF4] hover:shadow-[0_1px_3px_rgba(44,38,32,0.06)] focus-visible:ring-2 focus-visible:ring-midas-terracotta active:scale-[0.998] motion-reduce:transition-none motion-reduce:active:scale-100"
      >
        {/* Time column — fixed width so all rows align.
            §6.6 audit: width 56 → 76 to give the time + DD.MM stack the
            breathing room the spec shows. */}
        <div className="flex w-[76px] flex-shrink-0 flex-col gap-0.5">
          <span
            className="font-mono text-[13px] font-medium leading-[16px] text-midas-graphite"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {time}
          </span>
          <span
            className="font-mono text-[10px] uppercase tracking-[0.06em] text-midas-muted-stone"
            style={{ fontFamily: "'JetBrains Mono', monospace" }}
          >
            {sub}
          </span>
        </div>

        {/* Title + machine chip stack */}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {/* Machine context chip (or Platform Mode) */}
          {machineLabel ? (
            <span
              className="block w-full overflow-hidden whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.08em] text-midas-muted-stone"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {machineLabel}
            </span>
          ) : (
            <span
              className="flex w-full items-center gap-1 overflow-hidden whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.08em] text-midas-muted-stone"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              <span
                aria-hidden="true"
                className="inline-block h-[5px] w-[5px] flex-shrink-0 rounded-full bg-midas-terracotta"
              />
              <span>{t("midas.historyPage.platformMode", "Platform Mode")}</span>
            </span>
          )}
          <span
            // §6.13 audit: thread title 14px → 16px (line-height 20 → 22)
            // to match the spec's larger primary-text size.
            className="block w-full overflow-hidden text-ellipsis whitespace-nowrap font-sora text-[16px] font-medium leading-[22px] text-midas-graphite"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            {thread.title}
          </span>
        </div>

        {/* Trailing arrow.
            §6.18 audit: dropped the `mt-1` — combined with row-level
            items-center (§6.19), the arrow is now properly vertically
            centered in the card. */}
        <span
          aria-hidden="true"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center text-midas-warm-graphite transition-all duration-150 group-hover:text-midas-terracotta motion-reduce:transition-none"
        >
          <ArrowRight
            className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
            strokeWidth={1.75}
          />
        </span>
      </button>
    </li>
  );
};

export default MidasHistoryRowV2;
